import { inject, Injectable, Service, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { ActiveChannelDTO } from '../models/channel.model';
import { PointOfSaleDto } from '../models/point-of-sale.model';
import { Shop } from '../models/shop.model';
import { StorageContextModel } from '../models/storage-context.model';
import { NavigationStore } from './navigation.store';
import { SessionStore } from './session.store';

/**
 * Store di dominio per il contesto shop (ADR 002): stores disponibili, shop
 * corrente, canali attivati, canale corrente, punto vendita, nome canale.
 * Fonte di verità: i signal (senza `equal` custom, come i getter storici:
 * ultimo riferimento scritto). Ogni scrittura passa dai setter.
 */
@Service()
export class ShopContextStore {
  private readonly _titleService = inject(Title);
  private readonly _navigation = inject(NavigationStore);
  private readonly _session = inject(SessionStore);

  private readonly _shops = signal<Shop[] | null>(null);
  readonly shops = this._shops.asReadonly();

  private readonly _currentShop = signal<Shop | null>(null);
  readonly currentShop = this._currentShop.asReadonly();

  private readonly _activatedChannels = signal<ActiveChannelDTO[] | null>(null);
  readonly activatedChannels = this._activatedChannels.asReadonly();

  private readonly _currentActivatedChannel = signal<ActiveChannelDTO | null>(null);
  readonly currentActivatedChannel = this._currentActivatedChannel.asReadonly();

  private readonly _currentPos = signal<PointOfSaleDto | null>(null);
  readonly currentPos = this._currentPos.asReadonly();

  private readonly _currentChannelName = signal<string>('');
  readonly currentChannelName = this._currentChannelName.asReadonly();

  private readonly _shopLoading = signal<boolean>(false);
  readonly shopLoading = this._shopLoading.asReadonly();

  setShops(shops: Shop[]): void {
    this._shops.set(shops);
  }

  /** Side effect di parità con CoreService.setCurrentShop: localStorage, title, favicon, colore tema. */
  setCurrentShop(shop: Shop): void {
    localStorage.setItem('shopId', shop?.id?.toString());
    this._titleService.setTitle(shop.name);
    const favIcon: HTMLLinkElement = document.querySelector('#appIcon');
    if (favIcon) favIcon.href = shop?.faviconMediaUrl || 'favicon.ico';
    this._currentShop.set(shop);
    // Dopo l'aggiornamento dello stato, come la subscription storica in ContextService.
    this.applyPrimaryColor(shop);
  }

  /**
   * Scrive il colore primario dello shop nella CSS custom property `--primary`
   * (formato tripletta "r, g, b", che permette `rgba(var(--primary), α)` negli stili).
   *
   * Stava nel costruttore di ContextService come subscription: viveva solo se qualcuno
   * iniettava quel servizio. Qui si applica perché lo shop è cambiato, non per effetto
   * collaterale di un'iniezione. Sincrono di proposito: un effect() girerebbe al flush
   * del change detection, lasciando visibile per un tick il colore precedente.
   */
  private applyPrimaryColor(shop: Shop): void {
    if (!shop) return;
    document.documentElement.style.setProperty('--primary', this.rgbFromHex(shop.primaryColor));
  }

  /** Hex (#rrggbb) → "r, g, b". `null` se l'input non è un hex valido: la property viene rimossa e vale il fallback #111. */
  private rgbFromHex(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? parseInt(result[1], 16).toString() +
          ', ' +
          parseInt(result[2], 16).toString() +
          ', ' +
          parseInt(result[3], 16).toString()
      : null;
  }

  setChannels(channels: ActiveChannelDTO[]): void {
    this._activatedChannels.set(channels);
  }

  /** Side effect di parità con CoreService.setCurrentChannel. */
  setCurrentChannel(channel: ActiveChannelDTO): void {
    localStorage.setItem('channelId', channel?.id?.toString());

    if (channel?.channel?.supportsStockLimits) {
      this._navigation.setOnlyRetailMode(true, { skipLocalStorageUpdate: true });
    }

    this._currentActivatedChannel.set(channel);
    this.setCurrentChannelName(channel);
  }

  /** Side effect di parità con CoreService.setCurrentPos. */
  setCurrentPos(pos: PointOfSaleDto): void {
    this._currentPos.set(pos);
    if (pos) {
      const channels = pos.activatedStoreChannels
        || pos.storePointOfSales?.find(sp => sp.store?.id === this.currentShop().id)?.store?.activatedStoreChannels
        || [];
      this.setChannels(channels.filter(c => c.storeId === this.currentShop().id));
      if (this.currentShop()) {
        const context: StorageContextModel = {
          posId: pos.id,
          userId: this._session.userInfo()?.id
        };
        localStorage.setItem('4shop-context-store-' + this.currentShop().id, JSON.stringify(context));
      }
    }
  }

  /** Parità con CoreService.setShopLoading: nessuna emissione se il valore non cambia. */
  setShopLoading(value: boolean): void {
    if (this._shopLoading() === value) {
      return;
    }
    this._shopLoading.set(value);
  }

  // --- Helper di lookup (da ContextService; stessa semantica, unguarded incluso) ---

  getChannelsFromStore(storeId: number): ActiveChannelDTO[] | null {
    return this.shops().find(x => x.id === storeId)?.activatedStoreChannels ?? null;
  }

  getChannelById(channelId: number): ActiveChannelDTO {
    return this.activatedChannels().find(x => x.id === channelId);
  }

  getShopById(shopId: number): Shop {
    return this.shops().find(x => x.id === shopId);
  }

  private setCurrentChannelName(channel: ActiveChannelDTO): void {
    if (channel && channel.channel.supportsPreOrders) {
      this._currentChannelName.set(channel.channel.name);
    }
  }
}
